import React, { createContext, useContext, useState, useMemo } from "react";

/**
 * Parâmetros ajustáveis do simulador (Bloco E da aba "PARÂMETROS" do Excel —
 * as "células amarelas"). Compartilhados via Context para que qualquer aba
 * (Dashboard, Simulação, Impacto Caixa, Transição Anual, Formação de Preço)
 * recalcule instantaneamente quando o usuário edita um valor em Parâmetros,
 * exatamente como uma planilha viva.
 *
 * Valores em percentual (0-100), como exibido nos inputs do Bloco E.
 */
export const DEFAULT_PARAMS = {
  cbsPlena2033: 8.8,
  ibsSpPlena2033: 17.7,
  pctReceitaVeiculos: 65.0,
  pctCogsVeiculos: 97.0,
  pctCogsPecasServ: 70.0,
  issAtualServicos: 5.0,
};

// Os ids mantêm os nomes da planilha de origem; na tela, "veículos" é o grupo com ST ou
// monofásico, e "peças" são os demais produtos, para valer para qualquer tipo de produto.
export const PARAM_META = [
  { id: "cbsPlena2033", label: "CBS Plena (2033)", unit: "%", caption: "8,8% — federal, estimada" },
  { id: "ibsSpPlena2033", label: "IBS SP Plena (2033)", unit: "%", caption: "17,7% — estimativa estado+município SP" },
  { id: "pctReceitaVeiculos", label: "% Receita em produtos com ST", unit: "%", caption: "Restante é considerado demais produtos e serviços" },
  { id: "pctCogsVeiculos", label: "% COGS produtos com ST / Receita", unit: "%", caption: "Custo da mercadoria vendida sobre a receita de produtos com ST ou monofásicos" },
  { id: "pctCogsPecasServ", label: "% COGS demais / Receita", unit: "%", caption: "Custo da mercadoria vendida sobre a receita de demais produtos e serviços" },
  { id: "issAtualServicos", label: "ISS Atual (% Serviços)", unit: "%", caption: "Alíquota municipal sobre os 10% de receita de serviços" },
];

const ReformaParamsContext = createContext(null);

export function ReformaParamsProvider({ children }) {
  const [params, setParams] = useState(DEFAULT_PARAMS);

  // Inputs por segmento da Formação de Preço ({ [segmentoId]: { custo,
  // pctFrete, ... } }). Editados na aba Parâmetros e lidos (somente
  // leitura) na aba Formação de Preço — a aba virou dashboard.
  const [formacaoInputs, setFormacaoInputs] = useState({});

  const setParam = (id, value) => {
    setParams((prev) => ({ ...prev, [id]: value === "" ? "" : Number(value) }));
  };

  // Inicializa os inputs de um segmento com seus defaults — idempotente:
  // não sobrescreve o que o usuário já editou em Parâmetros.
  const initFormacaoSegment = (segmentId, defaults) => {
    setFormacaoInputs((prev) =>
      prev[segmentId] ? prev : { ...prev, [segmentId]: { ...defaults } }
    );
  };

  const setFormacaoInput = (segmentId, field, value) => {
    setFormacaoInputs((prev) => ({
      ...prev,
      [segmentId]: {
        ...(prev[segmentId] || {}),
        [field]: value === "" ? "" : Number(value),
      },
    }));
  };

  const resetParams = () => setParams(DEFAULT_PARAMS);

  const value = useMemo(
    () => ({ params, setParam, resetParams, formacaoInputs, initFormacaoSegment, setFormacaoInput }),
    [params, formacaoInputs]
  );

  return (
    <ReformaParamsContext.Provider value={value}>
      {children}
    </ReformaParamsContext.Provider>
  );
}

export function useReformaParams() {
  const ctx = useContext(ReformaParamsContext);
  if (!ctx) {
    throw new Error("useReformaParams deve ser usado dentro de ReformaParamsProvider");
  }
  return ctx;
}
